import { useState, useEffect, useRef, useCallback, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useSearch } from '@/lib/hooks/use-collections';
import { FocusTrapWrapper } from '@/components/FocusTrapWrapper';
import { useIntl } from '@/lib/intl';
import { CollectionProduct } from '@/domain/entities/collection.entity';
import {
  Overlay,
  ModalPanel,
  InputRow,
  SearchIconWrapper,
  ModalInput,
  CloseButton,
  ResultsContainer,
  ResultItem,
  ResultImage,
  ResultImagePlaceholder,
  ResultInfo,
  ResultTitle,
  ResultPrice,
  ModalFooter,
  ViewAllLink,
  StatusMessage,
} from './SearchModal.styled';

const DEBOUNCE_MS = 300;
const MAX_RESULTS = 5;
const MIN_QUERY_LENGTH = 2;

const SearchSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CloseSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ImageSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width={24} height={24}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef?: RefObject<HTMLElement>;
}

export function SearchModal({ isOpen, onClose, triggerRef }: Readonly<SearchModalProps>) {
  const navigate = useNavigate();
  const { localizedPath } = useIntl();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, loading, search } = useSearch();

  // Reset query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [isOpen]);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      const raf = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  // Debounce search trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Fire search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length >= MIN_QUERY_LENGTH) {
      search({ q: debouncedQuery, limit: MAX_RESULTS });
    }
  }, [debouncedQuery, search]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = query.trim();
      if (!trimmed) return;
      onClose();
      navigate(localizedPath(`/search?q=${encodeURIComponent(trimmed)}`));
    },
    [query, navigate, localizedPath, onClose]
  );

  const handleProductClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!isOpen) return null;

  const products: CollectionProduct[] =
    debouncedQuery.length >= MIN_QUERY_LENGTH ? (data?.products ?? []).slice(0, MAX_RESULTS) : [];
  const hasResults = products.length > 0;
  const showEmptyState =
    !loading && debouncedQuery.length >= MIN_QUERY_LENGTH && !hasResults;
  const showResults = !loading && hasResults;
  const searchPath = localizedPath(`/search?q=${encodeURIComponent(query.trim())}`);

  return createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="Search products">
      <ModalPanel onClick={stopPropagation}>
        <FocusTrapWrapper isOpen={isOpen} onClose={onClose} triggerRef={triggerRef}>
          <form onSubmit={handleSubmit}>
            <InputRow>
              <SearchIconWrapper aria-hidden="true">
                <SearchSvg />
              </SearchIconWrapper>

              <ModalInput
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products..."
                aria-label="Search products"
                autoComplete="off"
              />

              <CloseButton type="button" onClick={onClose} aria-label="Close search">
                <CloseSvg />
              </CloseButton>
            </InputRow>
          </form>

          {loading && debouncedQuery.length >= MIN_QUERY_LENGTH && (
            <StatusMessage>Searching...</StatusMessage>
          )}

          {showEmptyState && (
            <StatusMessage>No products found for &quot;{debouncedQuery}&quot;</StatusMessage>
          )}

          {showResults && (
            <>
              <ResultsContainer role="listbox" aria-label="Search results">
                {products.map((product) => (
                  <ResultItem
                    key={product.id}
                    to={localizedPath(`/products/${product.slug}`)}
                    onClick={handleProductClick}
                    role="option"
                    aria-selected="false"
                  >
                    {product.imageUri ? (
                      <ResultImage src={product.imageUri} alt={product.title} />
                    ) : (
                      <ResultImagePlaceholder aria-hidden="true">
                        <ImageSvg />
                      </ResultImagePlaceholder>
                    )}
                    <ResultInfo>
                      <ResultTitle>{product.title}</ResultTitle>
                      {product.priceMin > 0 && (
                        <ResultPrice>{formatPrice(product.priceMin)}</ResultPrice>
                      )}
                    </ResultInfo>
                  </ResultItem>
                ))}
              </ResultsContainer>

              {query.trim() && (
                <ModalFooter>
                  <ViewAllLink to={searchPath} onClick={handleProductClick}>
                    View all results for &quot;{query.trim()}&quot;
                  </ViewAllLink>
                </ModalFooter>
              )}
            </>
          )}
        </FocusTrapWrapper>
      </ModalPanel>
    </Overlay>,
    document.body
  );
}

export default SearchModal;
